import { useCreate } from "@refinedev/core";
import React, { useState, ReactNode } from 'react';
import { UploadMediaButton } from '@/components';
import { Box, LinearProgress } from "@mui/material";
import { uploadMultipleMedia } from "@/utils/uploadMedia";
import { MediaList } from "./list";
import { UploadResponse } from "@/utils/uploadToCdn";
import { IMedia } from "@/interfaces";

const MemoizedMediaList = React.memo(MediaList);

// Separate upload component
const UploadSection = React.memo(({ 
  allowMultiple = true,
  onUploadComplete 
}: { 
  allowMultiple?: boolean;
  onUploadComplete?: () => void;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { mutate: createMutate } = useCreate();

  // Memoize the upload handler to prevent recreation
  const handleUpload = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const results: UploadResponse[] = await uploadMultipleMedia(
        files, 
        (progress) => {
          setUploadProgress(progress);
        }
      );
      // Create medias entries using Refine's create mutation
      await Promise.all(results.map((file) => 
        createMutate({
          resource: "medias",
          values: {
            "filename": file.file_name,
            "resource_type": file.resource_type,
            "cdn_url": file.cdn_url,
            "size_in_kb": file.size_in_kb,
            "title": file.file_name,
            "type": file.type, 
          }
        })
      ));
        setTimeout(() => {
          onUploadComplete?.();
        }, 0);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [createMutate, onUploadComplete]);

  return (
    <Box sx={{ position: 'relative' }}>
      {isUploading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1
          }}
        />
      )}
      <UploadMediaButton
        isUploading={isUploading}
        onInputChange={handleUpload}
        uploadProgress={uploadProgress}
        allowMultiple={allowMultiple}
      />
    </Box>
  );
});

export const Media = ({ 
  enabledActions = { archive: true, view: true }, 
  children, 
  allowMultiple = true,
  onSelectionChange,
  formFieldName,
  currentMediaItems = []
}: {
  enabledActions?: Record<"archive" | "view", boolean>,
  children?: ReactNode,
  allowMultiple?: boolean,
  onSelectionChange?: (medias: IMedia[]) => void,
  formFieldName?: string,
  currentMediaItems?: IMedia[]
}) => {
  // Use useRef instead of useState for the refetch function
  const refetchRef = React.useRef<() => void>();

  const handleRefetch = React.useCallback(() => {
    setTimeout(() => {
      refetchRef.current?.();
    }, 0);
  }, []);

  return (
    <Box p={2}>
      <UploadSection 
        allowMultiple={allowMultiple} 
        onUploadComplete={handleRefetch}
      />
      <Box mt={2}>
        <MemoizedMediaList 
          enabledActions={enabledActions} 
          allowMultiple={allowMultiple}
          onRefetchReady={(refetch) => {
            refetchRef.current = refetch;
          }}
          onSelectionChange={onSelectionChange}
          formFieldName={formFieldName}
          currentMediaItems={currentMediaItems}
        />
      </Box>
      {children}
    </Box>
  );
};